import { useDebounce } from '@payaca/hooks';
import { useCallback, useReducer } from 'react';

type State = {
  searchQuery: string;
  debouncedSearchQuery: string;
  currentPage: number;
};

type Action =
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'APPLY_DEBOUNCED_SEARCH'; payload: string }
  | { type: 'SET_PAGE'; payload: number };

const initialState: State = {
  searchQuery: '',
  debouncedSearchQuery: '',
  currentPage: 1,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'APPLY_DEBOUNCED_SEARCH':
      // Only reset page if the search actually changed
      if (state.debouncedSearchQuery === action.payload) {
        return state;
      }
      return {
        ...state,
        debouncedSearchQuery: action.payload,
        currentPage: 1,
      };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    default:
      return state;
  }
}

export function useAddressTableState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  useDebounce(
    () => {
      dispatch({ type: 'APPLY_DEBOUNCED_SEARCH', payload: state.searchQuery });
    },
    300,
    [state.searchQuery]
  );

  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  return {
    searchQuery: state.searchQuery,
    debouncedSearchQuery: state.debouncedSearchQuery,
    currentPage: state.currentPage,
    setSearchQuery,
    setCurrentPage,
  };
}
